import { autoinject } from "aurelia-framework";
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm, State } from "app/shared/lib/essette-site";
import { Toaster } from 'app/shared/lib/toaster';
import { PreServiceDayService } from 'app/um/services/pre-service-day-service';
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import { getReference } from 'app/shared/models/essette-types';
import { apiCall } from 'app/shared/lib/api/api';
import { deepClone } from 'app/shared/lib/utilities';
import * as UM from 'app/um/models/essette-api';

export interface ISaveCareDayChanges {
    careDay: UM.AdmissionPreServiceDay;
}

@autoinject
export class SaveCareDay extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object protected careDay: UM.AdmissionPreServiceDay = { id: null };
    @bindable.boolean protected showActions: boolean = false;

    protected determinations: UM.AuthStatus[] = [];
    protected levelsOfCare: UM.LevelOfCare[] = [];
    private auth: UM.Auth | null = null;

    public submittedChanges: ISaveCareDayChanges | null = null;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        public state: State,
        private readonly toaster: Toaster,
        private readonly service: PreServiceDayService,
        private readonly context: AuthPageContext,
        ...rest: any[]) {
        super(...rest);
    }

    private bind(bindingContext: Object, overrideContext: Object): void {
        const authContext = bindingContext as AuthPageContext;
        this.auth = authContext.auth;
        this.loadLookups();
    }

    ////****************** Events ***************************************************////
    protected async careDayChanged(newValue: UM.AdmissionPreServiceDay | null): Promise<void> {
        if(!this.careDay.auth) {
            this.careDay.auth = this.auth;
        }
        this.loadLookups();
    }

    ////****************** Functions ************************************************////
    private validate(careDay: UM.AdmissionPreServiceDay): boolean {
        return true;
    }

    private constructSaveObject(): UM.AdmissionPreServiceDay {
        if(!this.careDay) {
            throw new Error('Bindable property "careDay" must be bound and include an id');
        }

        let careDay: UM.AdmissionPreServiceDay = {
            id: this.careDay.id || undefined,
            auth: getReference(this.careDay.auth),
            quantity: this.careDay.quantity,
            levelOfCare: {
                approved: getReference(this.careDay.levelOfCare && this.careDay.levelOfCare.approved),
                actual: getReference(this.careDay.levelOfCare && this.careDay.levelOfCare.actual)
            },
            determination: getReference(this.careDay.determination),
        }

        return careDay;
    }

    protected determinationChanged(changedDetermination: UM.AuthStatus | null): void {
        if(changedDetermination && changedDetermination.flag && changedDetermination.flag.isApproval && this.careDay && this.careDay.levelOfCare) {
            this.careDay.levelOfCare.approved = this.careDay.levelOfCare.actual;
        }
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        this.submittedChanges = null;

        const saveCareDay = this.constructSaveObject();
        if(this.validate(saveCareDay)) {
            return await this.saveCareDay(saveCareDay!);
        }
        else {
            return false;
        }
    }

    public reset(): void {
        this.careDay = { id: null };
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLookups(): Promise<void> {
        if(!this.auth || !this.auth.id) {
            return;
        }

        try {
            this.state.beginLoad();

            const [determinations, levelsOfCare] = await Promise.all(
                [
                    this.service.loadDeterminations(undefined, undefined, ['description']),
                    this.service.loadLevelsOfCare(undefined, undefined, ['description'])
                ]);

            this.determinations = determinations.results;
            this.levelsOfCare = levelsOfCare.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveCareDay(careDay: UM.AdmissionPreServiceDay): Promise<boolean> {
        try {
            this.state.beginSave();

            let response;
            if(!careDay.id) {
                response = await this.service.insertAdmissionPreServiceDay(careDay);
            }
            else {
                response = await this.service.updateAdmissionPreServiceDay(careDay);
            }

            this.toaster.standard.saved('um:auth.careDays.$', response.isSuccessful);
            if(response.isSuccessful) {
                this.submittedChanges = { careDay: deepClone(careDay) };
                this.reset();
                this.context.triggerCorrespondenceRules();
                return true;
            }
            else {
                this.logger.error(response.resultText!);
                return false;
            }

        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
        }
    }
}

